import { useQuery } from '@tanstack/react-query'
import { coreServicesQuery, type DeviceCoreServicesPage } from '../api/devices'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'
import './DeviceCoreServices.css'

/**
 * Fila Core Services - coreServicesMonitoring.php (banda Summary)
 * + coreServicesSummary.php (Role / Status / Focus / Upgrade). Numai citire.
 */

function SummaryBody({ page }: { page: DeviceCoreServicesPage }) {
  if (!page.available || !page.role || !page.status || !page.focus || !page.upgrade || !page.primary) {
    return (
      <div className="csm-empty">
        <i className="fas fa-server fa-2x" style={{ marginBottom: 12, opacity: 0.5 }}></i>
        <div>Core Services is available on servers with a detected role.</div>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          No primary service role was inferred from running processes on this device.
        </div>
      </div>
    )
  }

  const others = page.role.others.length ? ` · ${page.role.others.join(' · ')}` : ''

  return (
    <>
      <div className="csm-board">
        <div className="csm-card role">
          <div className="csm-eyebrow">Role</div>
          <div className="csm-value">{page.role.label}</div>
          <div className="csm-foot">
            {page.role.foot}
            {others}
          </div>
        </div>
        <div className={`csm-card ${page.status.tone}`}>
          <div className="csm-eyebrow">Status</div>
          <div className="csm-value">{page.status.label}</div>
          <div className="csm-foot" title={page.status.title}>
            {page.status.foot}
          </div>
        </div>
        <div className={`csm-card ${page.focus.tone}`}>
          <div className="csm-eyebrow">Focus</div>
          <div className={page.focus.small ? 'csm-value small' : 'csm-value'}>{page.focus.display}</div>
          <div className="csm-foot">{page.focus.foot}</div>
        </div>
        <div className={`csm-card ${page.upgrade.tone}`}>
          <div className="csm-eyebrow">Upgrade</div>
          <div className="csm-value small" title={page.upgrade.title}>
            {page.upgrade.display}
          </div>
          <div className="csm-foot">{page.upgrade.foot}</div>
        </div>
      </div>

      {page.next && (
        <div className={`alert alert-${page.next.cls}`} style={{ marginBottom: 14 }}>
          <strong>Next:</strong> {page.next.text}
        </div>
      )}

      {page.reasons && page.reasons.length > 0 && (
        <div className="csm-section">
          <h4>Why this status</h4>
          <ul className="csm-reasons">
            {page.reasons.map((r, i) => (
              <li key={i}>
                <i className={`fas ${r.icon}`}></i> {r.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {page.show_hosted_vms && (
        <div className="csm-section">
          <h4>Virtual machines</h4>
          {page.hosted_vms.length === 0 ? (
            <div className="csm-vm-muted">
              No guests listed yet. After the host agent inventories, Hyper-V and KVM machines appear here with power
              and health, including those not in the fleet.
            </div>
          ) : (
            <table className="csm-vms">
              <thead>
                <tr>
                  <th>Machine</th>
                  <th>Power</th>
                  <th>Health</th>
                  <th>RAM</th>
                </tr>
              </thead>
              <tbody>
                {page.hosted_vms.map((vm, i) => (
                  <tr key={`${vm.name}-${i}`}>
                    <td>
                      {vm.href ? <a href={vm.href}>{vm.name}</a> : vm.name}
                    </td>
                    <td>
                      {vm.state_label ? (
                        <span className={`csm-vm-state csm-vm-${vm.power !== '' ? vm.power : 'off'}`}>{vm.state_label}</span>
                      ) : (
                        <span className="csm-vm-muted">Unknown</span>
                      )}
                    </td>
                    <td>
                      <span className={`csm-vm-health csm-health-${vm.tone}`}>{vm.health}</span>
                    </td>
                    <td>{vm.memory}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="csm-section">
        <h4>Primary service</h4>
        <div className="csm-signals">
          <div className="csm-signal">
            <span className="k">Name</span>
            <span className="v">{page.primary.name}</span>
          </div>
          <div className="csm-signal">
            <span className="k">Package</span>
            <span className="v">{page.primary.package}</span>
          </div>
          <div className="csm-signal">
            <span className="k">Installed</span>
            <span className="v">{page.primary.version}</span>
          </div>
          <div className="csm-signal">
            <span className="k">Available</span>
            <span className="v">{page.primary.available}</span>
          </div>
          <div className="csm-signal">
            <span className="k">Importance</span>
            <span className="v">{page.primary.importance}</span>
          </div>
          <div className="csm-signal">
            <span className="k">Updates</span>
            <span className="v">{page.primary.updates}</span>
          </div>
        </div>
      </div>

      {page.signals && page.signals.length > 0 && (
        <div className="csm-section">
          <h4>Signals</h4>
          <div className="csm-signals">
            {page.signals.map((s) => (
              <div className="csm-signal" key={s.label}>
                <span className="k">{s.label}</span>
                <span className="v">{s.display}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {page.config && (
        <div className="csm-section">
          <h4>
            Config review <small className="text-muted">{page.config.score}</small>
          </h4>
          {page.config.items.length ? (
            <ul className="csm-reasons">
              {page.config.items.map((item, i) => (
                <li key={i}>
                  {item.path}
                  {item.note !== '' && (
                    <>
                      {' '}
                      — <span className="text-muted">{item.note}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-muted">No config drift reported for this role.</div>
          )}
        </div>
      )}

      {page.optimizations && page.optimizations.length > 0 && (
        <div className="csm-section">
          <h4>Optimizations</h4>
          <ul className="csm-reasons">
            {page.optimizations.map((opt, i) => (
              <li key={i}>{opt}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

export function DeviceCoreServices({ id }: ScreenProps) {
  const page = useQuery(coreServicesQuery(id))

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="device-core-services">
      <div className="row">
        <div className="col-md-12">
          <ul className="nav nav-tabs line-tabs mb-2 csmList" role="tablist">
            <li className="active">
              {/* Adresa veche ramane in markup ca fila sa arate identic cu
                  cea din CI3; clicul e prins aici. Summary e singura fila si
                  e deja deschisa, deci nu are unde duce - iar
                  Proactivitee::coreServicesSummary a plecat din CodeIgniter. */}
              <a href={`/proactivitee/coreServicesSummary/${id}`} onClick={(e) => e.preventDefault()}>
                <i className="fas fa-server"></i> Summary
              </a>
            </li>
          </ul>
          <div className="tab-content csm-tab-content">
            <SummaryBody page={page.data} />
          </div>
        </div>
      </div>
    </div>
  )
}
